import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations, defaultLanguage } from '../data/localization';
import { getLangFromPath, isLocalizablePath, localizedPath } from '../utils/langPath';

const LanguageContext = createContext();

// Public SEO pages take their language from the URL (/ru/... = ru) so the
// prerendered HTML and the hydrated app always agree; other pages (admin,
// legal) fall back to the saved preference.
const getInitialLanguage = () => {
  try {
    const path = window.location.pathname;
    if (isLocalizablePath(path)) return getLangFromPath(path);
    // react-snap shares one browser profile across pages — never let a /ru
    // page's saved choice leak into prerendered uk-only pages (legal, 404)
    if (navigator.userAgent === 'ReactSnap') return defaultLanguage;
    return localStorage.getItem('language') || defaultLanguage;
  } catch {
    return defaultLanguage;
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(getInitialLanguage);

  useEffect(() => {
    try {
      localStorage.setItem('language', language);
    } catch {
      // storage unavailable (private mode) — language still works for this session
    }
  }, [language]);

  const t = (path) => {
    const keys = path.split('.');
    let result = translations[language] || translations[defaultLanguage];

    for (const key of keys) {
      result = result?.[key];
    }

    return result || path;
  };

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === 'uk' ? 'ru' : 'uk'));
  };

  // Language-aware internal link target, e.g. lp('/about') → '/ru/about' on Russian pages
  const lp = useCallback((path) => localizedPath(path, language), [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t, lp }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
